import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import { toast } from 'sonner';
import {
  Search,
  Download,
  Calendar,
  FileText,
  DollarSign,
  Car,
  Users,
  Clock,
  MapPin,
  Star,
  CheckCircle,
  XCircle,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import DataTablePagination from '@/components/ui/data-table-pagination';
import {
  useRideHistory,
  useExportRideHistoryPdf,
  useExportRideHistoryExcel,
  useRideAnalysis,
  useRevenueRideTrend,
  useRevenueBreakdown,
  useDriverPerformanceTiers,
  useCompletedRidesHourly,
} from '@/api-services/hooks/useReports';
import { ApiResponse, PagedData } from '@/api-services/hooks/types';
import type { RideHistoryItem } from '@/api-services/hooks/reportsTypes';

// Helper function to extract data from API response
const extractData = <T,>(response: ApiResponse<T> | null): T | null => {
  if (!response) return null;
  if (response.data) return response.data as T;
  return null;
};

// Helper function to format currency
const formatCurrency = (amount: number | undefined): string => {
  if (amount === undefined || amount === null) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Helper function to format number
const formatNumber = (num: number | undefined): string => {
  if (num === undefined || num === null) return '0';
  return new Intl.NumberFormat('en-US').format(num);
};

// Helper function to get date range based on selection
const getDateRange = (range: string): { from?: string; to?: string } => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  switch (range) {
    case 'today':
      return {
        from: new Date(today).toISOString(),
        to: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1).toISOString(),
      };
    case 'week':
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - today.getDay());
      return {
        from: weekStart.toISOString(),
        to: now.toISOString(),
      };
    case 'month':
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      return {
        from: monthStart.toISOString(),
        to: now.toISOString(),
      };
    case 'quarter':
      const quarterStart = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
      return {
        from: quarterStart.toISOString(),
        to: now.toISOString(),
      };
    case 'year':
      const yearStart = new Date(today.getFullYear(), 0, 1);
      return {
        from: yearStart.toISOString(),
        to: now.toISOString(),
      };
    default:
      return {};
  }
};

const ReportsPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState('week');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Get date range for API calls
  const dateRangeParams = useMemo(() => getDateRange(dateRange), [dateRange]);

  // Fetch reports data
  const { data: rideHistoryResponse, loading: rideHistoryLoading, error: rideHistoryError, refetch: refetchRideHistory } = useRideHistory(
    {
      ...dateRangeParams,
      pageable: {
        page: currentPage - 1,
        size: itemsPerPage,
      },
    }
  );

  const { data: rideAnalysisResponse, loading: analysisLoading, error: analysisError } = useRideAnalysis(dateRangeParams);
  const { data: revenueTrendResponse, loading: trendLoading, error: trendError } = useRevenueRideTrend();
  const { data: revenueBreakdownResponse, loading: breakdownLoading, error: breakdownError } = useRevenueBreakdown();
  const { data: driverPerformanceResponse, loading: driverPerfLoading, error: driverPerfError } = useDriverPerformanceTiers();
  const { data: completedRidesHourlyResponse, loading: hourlyLoading, error: hourlyError } = useCompletedRidesHourly();

  // Export mutations
  const exportPdf = useExportRideHistoryPdf({
    onSuccess: () => {
      toast.success('PDF report exported successfully');
    },
    onError: (error) => {
      toast.error(`Failed to export PDF: ${error.message}`);
    },
  });

  const exportExcel = useExportRideHistoryExcel({
    onSuccess: () => {
      toast.success('Excel report exported successfully');
    },
    onError: (error) => {
      toast.error(`Failed to export Excel: ${error.message}`);
    },
  });

  // Extract data from responses
  const rideHistoryData = extractData(rideHistoryResponse) as PagedData<RideHistoryItem> | null;
  const rideHistoryItems = rideHistoryData?.content || [];
  const totalRides = rideHistoryData?.totalElements || 0;
  
  const rideAnalysisData = extractData(rideAnalysisResponse);
  const revenueTrendData = extractData(revenueTrendResponse) || [];
  const revenueBreakdownData = extractData(revenueBreakdownResponse) || [];
  const driverPerformanceData = extractData(driverPerformanceResponse) || [];
  const completedRidesHourlyData = extractData(completedRidesHourlyResponse) || [];

  // Filter rides locally (client-side filtering for search and status)
  const filteredRides = useMemo(() => {
    return rideHistoryItems.filter((ride: RideHistoryItem) => {
      const matchesSearch =
        !searchTerm ||
        String(ride.rideId || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (ride.riderName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (ride.driverName || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus =
        statusFilter === 'all' ||
        (ride.status || '').toLowerCase() === statusFilter.toLowerCase();
      
      return matchesSearch && matchesStatus;
    });
  }, [rideHistoryItems, searchTerm, statusFilter]);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    const total = rideAnalysisData?.totalRides || totalRides || 0;
    const completed = rideAnalysisData?.completedRides || 0;
    const cancelled = rideAnalysisData?.cancelledRides || 0;
    const avgDuration = rideAnalysisData?.averageDuration || 0;
    
    return { total, completed, cancelled, avgDuration };
  }, [rideAnalysisData, totalRides]);

  const getStatusBadge = (status: string | undefined) => {
    const statusLower = (status || '').toLowerCase();
    const variants: Record<string, { className: string }> = {
      completed: { className: 'bg-success text-success-foreground' },
      cancelled: { className: 'bg-destructive text-destructive-foreground' },
      in_progress: { className: 'bg-warning text-warning-foreground' },
      'in progress': { className: 'bg-warning text-warning-foreground' },
    };
    return variants[statusLower] || variants.completed;
  };

  const handleExportReport = async (type: string) => {
    if (type === 'Ride History' || type === 'All Reports') {
      // Show export options
      const format = await new Promise<'pdf' | 'excel'>((resolve) => {
        // For now, default to PDF, but you could show a dialog
        resolve('pdf');
      });
      
      if (format === 'pdf') {
        exportPdf.mutate(dateRangeParams);
      } else {
        exportExcel.mutate(dateRangeParams);
      }
    } else {
      toast.info(`${type} export coming soon`);
    }
  };

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, dateRange]);

  // Refetch when date range changes
  useEffect(() => {
    refetchRideHistory();
  }, [dateRange, currentPage, itemsPerPage]);

  const handlePageChange = (page: number) => setCurrentPage(page);
  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const isLoading = rideHistoryLoading || analysisLoading;
  const hasError = rideHistoryError || analysisError;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Reports & Analytics</h1>
          <p className="text-muted-foreground mt-1">Comprehensive reporting and data insights</p>
        </div>
        <div className="flex items-center space-x-2">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            onClick={() => handleExportReport('All Reports')} 
            className="bg-gradient-primary hover:opacity-90"
            disabled={exportPdf.loading || exportExcel.loading}
          >
            {(exportPdf.loading || exportExcel.loading) ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Export All
          </Button>
        </div>
      </div>

      {/* Error State */}
      {hasError && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              <p>Error loading reports data. Please try again later.</p>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="ride-history" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="ride-history">Ride History</TabsTrigger>
          <TabsTrigger value="financial">Financial Reports</TabsTrigger>
          <TabsTrigger value="operational">Operational Reports</TabsTrigger>
        </TabsList>

        {/* Ride History Tab */}
        <TabsContent value="ride-history" className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card className="hover-lift interactive">
              <CardContent className="p-6">
                {analysisLoading ? (
                  <div className="flex items-center justify-center h-16">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Total Rides</p>
                      <p className="text-2xl font-bold text-foreground">{formatNumber(summaryStats.total)}</p>
                    </div>
                    <Car className="h-8 w-8 text-chart-primary" />
                  </div>
                )}
              </CardContent>
            </Card>
            
            <Card className="hover-lift interactive">
              <CardContent className="p-6">
                {analysisLoading ? (
                  <div className="flex items-center justify-center h-16">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Completed</p>
                      <p className="text-2xl font-bold text-success">{formatNumber(summaryStats.completed)}</p>
                    </div>
                    <CheckCircle className="h-8 w-8 text-success" />
                  </div>
                )}
              </CardContent>
            </Card>
            
            <Card className="hover-lift interactive">
              <CardContent className="p-6">
                {analysisLoading ? (
                  <div className="flex items-center justify-center h-16">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Cancelled</p>
                      <p className="text-2xl font-bold text-destructive">{formatNumber(summaryStats.cancelled)}</p>
                    </div>
                    <XCircle className="h-8 w-8 text-destructive" />
                  </div>
                )}
              </CardContent>
            </Card>
            
            <Card className="hover-lift interactive">
              <CardContent className="p-6">
                {analysisLoading ? (
                  <div className="flex items-center justify-center h-16">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Avg Duration</p>
                      <p className="text-2xl font-bold text-foreground">
                        {summaryStats.avgDuration ? `${summaryStats.avgDuration.toFixed(1)}m` : '0m'}
                      </p>
                    </div>
                    <Clock className="h-8 w-8 text-chart-accent" />
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Filters and Search */}
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by ride ID, passenger, or driver..."
                    className="pl-10"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => handleExportReport('Ride History')} 
                    variant="outline"
                    disabled={exportPdf.loading}
                  >
                    {exportPdf.loading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    PDF
                  </Button>
                  <Button 
                    onClick={() => exportExcel.mutate(dateRangeParams)} 
                    variant="outline"
                    disabled={exportExcel.loading}
                  >
                    {exportExcel.loading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Excel
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Ride History Table */}
          <Card>
            <CardHeader>
              <CardTitle>Ride History ({filteredRides.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {rideHistoryLoading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : filteredRides.length === 0 ? (
                <div className="flex items-center justify-center h-64 text-muted-foreground">
                  <p>No ride history found</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Ride ID</TableHead>
                          <TableHead>Passenger</TableHead>
                          <TableHead>Driver</TableHead>
                          <TableHead>Route</TableHead>
                          <TableHead>Distance</TableHead>
                          <TableHead>Duration</TableHead>
                          <TableHead>Fare</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Rating</TableHead>
                          <TableHead>Time</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredRides.map((ride: RideHistoryItem) => {
                          const statusBadge = getStatusBadge(ride.status);
                          const fare = ride.fare || ride.totalFare || ride.amount || 0;
                          const riderName = ride.riderName || `Rider #${ride.riderId || 'N/A'}`;
                          const driverName = ride.driverName || `Driver #${ride.driverId || 'N/A'}`;
                          const time = ride.startTime || ride.completedAt || '';
                          
                          return (
                            <TableRow key={ride.rideId || Math.random()} className="hover:bg-muted/50">
                              <TableCell className="font-medium">#{ride.rideId || 'N/A'}</TableCell>
                              <TableCell>
                                <div className="flex items-center space-x-2">
                                  <Avatar className="w-6 h-6">
                                    <AvatarFallback className="text-xs">
                                      {riderName.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()}
                                    </AvatarFallback>
                                  </Avatar>
                                  <span className="text-sm">{riderName}</span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center space-x-2">
                                  <Avatar className="w-6 h-6">
                                    <AvatarFallback className="text-xs">
                                      {driverName.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()}
                                    </AvatarFallback>
                                  </Avatar>
                                  <span className="text-sm">{driverName}</span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="max-w-48">
                                  {ride.pickupLocation && (
                                    <div className="flex items-center text-sm">
                                      <MapPin className="w-3 h-3 mr-1 text-success" />
                                      <span className="truncate">{ride.pickupLocation}</span>
                                    </div>
                                  )}
                                  {ride.dropoffLocation && (
                                    <div className="flex items-center text-sm text-muted-foreground">
                                      <MapPin className="w-3 h-3 mr-1 text-destructive" />
                                      <span className="truncate">{ride.dropoffLocation}</span>
                                    </div>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>{ride.distance ? `${ride.distance.toFixed(1)} mi` : '-'}</TableCell>
                              <TableCell>{ride.duration ? `${ride.duration} min` : '-'}</TableCell>
                              <TableCell>{formatCurrency(fare)}</TableCell>
                              <TableCell>
                                <Badge className={statusBadge.className}>
                                  {(ride.status || '').charAt(0).toUpperCase() + (ride.status || '').slice(1)}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {ride.rating ? (
                                  <div className="flex items-center">
                                    <Star className="w-3 h-3 text-warning mr-1 fill-warning" />
                                    {ride.rating.toFixed(1)}
                                  </div>
                                ) : (
                                  '-'
                                )}
                              </TableCell>
                              <TableCell className="text-sm">
                                {time ? (
                                  <>
                                    {new Date(time).toLocaleDateString()}<br />
                                    {new Date(time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  </>
                                ) : (
                                  '-'
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {rideHistoryData && rideHistoryData.totalElements > 0 && (
                    <DataTablePagination
                      currentPage={currentPage}
                      totalPages={rideHistoryData.totalPages || 1}
                      totalItems={rideHistoryData.totalElements}
                      itemsPerPage={itemsPerPage}
                      onPageChange={handlePageChange}
                      onItemsPerPageChange={handleItemsPerPageChange}
                      className="mt-4"
                    />
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Financial Reports Tab */}
        <TabsContent value="financial" className="space-y-6">
          {/* Revenue Breakdown Chart */}
          <Card className="hover-lift">
            <CardHeader>
              <CardTitle>Revenue Breakdown (Last 6 Months)</CardTitle>
            </CardHeader>
            <CardContent>
              {breakdownLoading ? (
                <div className="flex items-center justify-center h-80">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : revenueBreakdownData.length === 0 ? (
                <div className="flex items-center justify-center h-80 text-muted-foreground">
                  <p>No revenue data available</p>
                </div>
              ) : (
                <div className="h-80 chart-enter">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={revenueBreakdownData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" />
                      <YAxis stroke="hsl(var(--muted-foreground))" />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: 'hsl(var(--popover))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                      />
                      <Bar dataKey="grossRevenue" fill="hsl(var(--chart-primary))" name="Gross Revenue" />
                      <Bar dataKey="netRevenue" fill="hsl(var(--chart-secondary))" name="Net Revenue" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Revenue & Ride Trend Chart */}
          <Card className="hover-lift">
            <CardHeader>
              <CardTitle>Revenue & Ride Trend (Last 6 Months)</CardTitle>
            </CardHeader>
            <CardContent>
              {trendLoading ? (
                <div className="flex items-center justify-center h-80">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : revenueTrendData.length === 0 ? (
                <div className="flex items-center justify-center h-80 text-muted-foreground">
                  <p>No trend data available</p>
                </div>
              ) : (
                <div className="h-80 chart-enter">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={revenueTrendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" />
                      <YAxis yAxisId="left" stroke="hsl(var(--muted-foreground))" />
                      <YAxis yAxisId="right" orientation="right" stroke="hsl(var(--muted-foreground))" />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: 'hsl(var(--popover))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                      />
                      <Line yAxisId="left" type="monotone" dataKey="revenue" stroke="hsl(var(--chart-primary))" name="Revenue" />
                      <Line yAxisId="right" type="monotone" dataKey="rides" stroke="hsl(var(--chart-secondary))" name="Rides" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Operational Reports Tab */}
        <TabsContent value="operational" className="space-y-6">
          {/* Driver Performance Tiers */}
          <Card>
            <CardHeader>
              <CardTitle>Driver Performance Tiers</CardTitle>
            </CardHeader>
            <CardContent>
              {driverPerfLoading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : driverPerformanceData.length === 0 ? (
                <div className="flex items-center justify-center h-64 text-muted-foreground">
                  <p>No driver performance data available</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {driverPerformanceData.map((tier: any, index: number) => (
                    <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <p className="font-semibold">{tier.tierName || tier.tier || `Tier ${index + 1}`}</p>
                        <p className="text-sm text-muted-foreground">
                          {tier.driverCount || 0} drivers
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Avg Rating</p>
                        <p className="font-semibold">{tier.averageRating?.toFixed(1) || 'N/A'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Total Rides</p>
                        <p className="font-semibold">{formatNumber(tier.totalRides)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Revenue</p>
                        <p className="font-semibold">{formatCurrency(tier.totalRevenue)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Completed Rides Hourly */}
          <Card>
            <CardHeader>
              <CardTitle>Completed Rides (4-Hour Intervals)</CardTitle>
            </CardHeader>
            <CardContent>
              {hourlyLoading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : completedRidesHourlyData.length === 0 ? (
                <div className="flex items-center justify-center h-64 text-muted-foreground">
                  <p>No hourly data available</p>
                </div>
              ) : (
                <div className="h-80 chart-enter">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={completedRidesHourlyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="timeSlot" stroke="hsl(var(--muted-foreground))" />
                      <YAxis stroke="hsl(var(--muted-foreground))" />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: 'hsl(var(--popover))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                      />
                      <Bar dataKey="completedRides" fill="hsl(var(--chart-primary))" name="Completed Rides" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsPage;
